import {
  GAME,
  UPGRADES,
  upgradeCost,
  type UpgradeId,
  type UpgradeDef,
} from '../config';

export type UpgradeLevels = Record<UpgradeId, number>;

export type SaveData = {
  cash: number;
  levels: UpgradeLevels;
  milestoneIndex: number;
  version: 2;
};

export function defaultLevels(): UpgradeLevels {
  return {
    stack: 0,
    speed: 0,
    bees: 0,
    honeyRate: 0,
    clerk: 0,
    clerkCarry: 0,
    queue: 0,
    fields: 0,
    prices: 0,
    storeroom: 0,
    flowers: 0,
  };
}

export function loadSave(): SaveData | null {
  try {
    const raw = localStorage.getItem(GAME.saveKey);
    if (!raw) return null;
    const data = JSON.parse(raw) as SaveData;
    if (data.version !== 2) return null;
    return data;
  } catch {
    return null;
  }
}

export function writeSave(data: SaveData): void {
  try {
    localStorage.setItem(GAME.saveKey, JSON.stringify(data));
  } catch {
    // ignore quota
  }
}

export function getDef(id: UpgradeId): UpgradeDef {
  return UPGRADES.find((u) => u.id === id)!;
}

export function costFor(id: UpgradeId, level: number): number {
  return upgradeCost(getDef(id), level);
}

export function canBuy(id: UpgradeId, levels: UpgradeLevels, cash: number): boolean {
  const def = getDef(id);
  if (levels[id] >= def.maxLevel) return false;
  return cash >= costFor(id, levels[id]);
}

export type DerivedStats = {
  stackMax: number;
  speed: number;
  beeCount: number;
  honeyInterval: number;
  clerkCount: number;
  clerkCarry: number;
  clerkSpeed: number;
  maxQueue: number;
  customerInterval: number;
  storeroomCap: number;
  pricesLevel: number;
  flowersLevel: number;
  fieldsLevel: number;
  worldHalf: number;
};

export function deriveStats(levels: UpgradeLevels): DerivedStats {
  return {
    stackMax: GAME.baseStackMax + levels.stack * 6,
    speed: GAME.playerSpeed * (1 + levels.speed * 0.08),
    beeCount: GAME.baseBeeCount + levels.bees,
    honeyInterval: Math.max(0.7, GAME.baseHoneyInterval * Math.pow(0.9, levels.honeyRate)),
    clerkCount: 1 + levels.clerk,
    clerkCarry: GAME.baseEmployeeCarry + levels.clerkCarry * 3,
    clerkSpeed: GAME.baseEmployeeSpeed * (1 + levels.clerkCarry * 0.04 + levels.clerk * 0.06),
    maxQueue: GAME.baseMaxQueue + levels.queue,
    customerInterval: Math.max(1.1, GAME.baseCustomerInterval * Math.pow(0.92, levels.queue)),
    storeroomCap: GAME.baseStoreroom + levels.storeroom * 12,
    pricesLevel: levels.prices,
    flowersLevel: levels.flowers,
    fieldsLevel: levels.fields,
    worldHalf: GAME.baseWorldHalf + levels.fields * 3.5,
  };
}

/** Human-readable current → next for upgrade panel (level = current level before buy). */
export function effectPreview(id: UpgradeId, level: number): string {
  switch (id) {
    case 'stack':
      return `Stack ${GAME.baseStackMax + level * 6} → ${GAME.baseStackMax + (level + 1) * 6}`;
    case 'speed':
      return `Speed ${100 + level * 8}% → ${100 + (level + 1) * 8}%`;
    case 'bees':
      return `Bees ${GAME.baseBeeCount + level} → ${GAME.baseBeeCount + level + 1}`;
    case 'honeyRate':
      return `Honey ~${(GAME.baseHoneyInterval * Math.pow(0.9, level)).toFixed(1)}s → ${(GAME.baseHoneyInterval * Math.pow(0.9, level + 1)).toFixed(1)}s`;
    case 'clerk':
      return `Clerks ${1 + level} → ${2 + level}`;
    case 'clerkCarry':
      return `Carry ${GAME.baseEmployeeCarry + level * 3} → ${GAME.baseEmployeeCarry + (level + 1) * 3}`;
    case 'queue':
      return `Queue ${GAME.baseMaxQueue + level} → ${GAME.baseMaxQueue + level + 1}`;
    case 'fields':
      return `Field ring ${level} → ${level + 1} unlocked`;
    case 'prices':
      return `Prices +${level * 10}% → +${(level + 1) * 10}%`;
    case 'storeroom':
      return `Store ${GAME.baseStoreroom + level * 12} → ${GAME.baseStoreroom + (level + 1) * 12}`;
    case 'flowers':
      return `Catalog tier ${level} → ${level + 1}`;
    default:
      return 'Upgraded';
  }
}
